import { ArrowPathIcon, MapPinIcon, PaperClipIcon, UserIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { getGroupColor } from './groupColors';
import type { CalendarEventInstance } from './types';

function timeLabel(date: Date): string {
  const mins = date.getMinutes();
  const label = date
    .toLocaleTimeString(undefined, { hour: 'numeric', ...(mins ? { minute: '2-digit' } : {}) })
    .replace(' ', '')
    .toLowerCase();
  return label;
}

/**
 * At-a-glance RSVP "traffic-light trio": mini person icons with going /
 * maybe / can't counts. Hidden when nobody has responded yet.
 * `onSolid` renders white (for all-day chips on solid backgrounds).
 */
export function RsvpTrio({
  counts,
  onSolid,
}: {
  counts: { yes: number; no: number; maybe: number };
  onSolid?: boolean;
}) {
  const total = counts.yes + counts.maybe + counts.no;
  if (total === 0) return null;
  const items = [
    { count: counts.yes, className: onSolid ? 'text-white' : 'text-green-700' },
    { count: counts.maybe, className: onSolid ? 'text-white/80' : 'text-amber-600' },
    { count: counts.no, className: onSolid ? 'text-white/60' : 'text-red-600' },
  ];
  return (
    <span
      className="mt-0.5 flex items-center gap-2"
      aria-label={`${counts.yes} going, ${counts.maybe} maybe, ${counts.no} can't go`}
    >
      {items.map((item, i) => (
        <span
          key={i}
          className={clsx('flex items-center gap-0.5 text-[10px] font-semibold tabular-nums', item.className)}
        >
          <UserIcon className="h-3 w-3" aria-hidden="true" />
          {item.count}
        </span>
      ))}
    </span>
  );
}

interface Props {
  instance: CalendarEventInstance;
  variant: 'chip' | 'row';
  onClick: () => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
  /** Show the group pill on row variant (hidden for single-group orgs). */
  showGroup?: boolean;
}

/**
 * One event on the calendar. 'chip' is the compact month-grid form;
 * 'row' is the agenda / day-list form.
 */
export default function EventChip({
  instance,
  variant,
  onClick,
  onMouseEnter,
  onMouseLeave,
  showGroup = true,
}: Props) {
  const color = getGroupColor(instance.groupId);

  if (variant === 'chip') {
    return (
      <button
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={clsx(
          'block w-full rounded-md px-1.5 py-1 text-left transition hover:ring-1 hover:ring-inset',
          instance.allDay ? color.solidBg : color.chipBg,
          instance.allDay ? 'hover:brightness-110' : 'hover:ring-current'
        )}
      >
        <span
          className={clsx(
            'block truncate text-[11px] font-medium leading-tight',
            instance.allDay ? color.solidText : color.chipText
          )}
        >
          {!instance.allDay && (
            <span className="mr-1 font-normal opacity-75">
              {timeLabel(instance.occurrenceStart)}
            </span>
          )}
          {instance.title}
        </span>
        <RsvpTrio counts={instance.rsvpCounts} onSolid={instance.allDay} />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-flock-gray-200 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-flock-navy-300"
    >
      <span className="w-16 shrink-0 text-xs font-medium tabular-nums text-flock-navy-500">
        {instance.allDay ? 'All day' : timeLabel(instance.occurrenceStart)}
      </span>
      <span className={clsx('h-8 w-1 shrink-0 rounded-full', color.dot)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-flock-navy-900">
            {instance.title}
          </span>
          {instance.recurrenceRule && (
            <ArrowPathIcon className="h-3 w-3 shrink-0 text-flock-navy-300" title="Repeats" />
          )}
          {instance.attachmentCount > 0 && (
            <span
              className="flex shrink-0 items-center gap-0.5 text-[10px] text-flock-navy-400"
              title={`${instance.attachmentCount} attachment${instance.attachmentCount === 1 ? '' : 's'}`}
            >
              <PaperClipIcon className="h-3 w-3" />
              {instance.attachmentCount}
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-[11px] text-flock-navy-400">
          {showGroup && (
            <span className={clsx('rounded-full px-1.5 py-px font-medium', color.chipBg, color.chipText)}>
              {instance.groupName}
            </span>
          )}
          {instance.location && (
            <span className="flex min-w-0 items-center gap-0.5 truncate">
              <MapPinIcon className="h-3 w-3 shrink-0" /> {instance.location}
            </span>
          )}
        </span>
      </span>
      <RsvpTrio counts={instance.rsvpCounts} />
    </button>
  );
}
